// Train: the positions from your own games still waiting to be solved,
// newest first, one at a time.
//
// A puzzle has an address — the game, the side, the move — and Train writes
// the one it is showing into the address bar, and opens on the one it finds
// there. Each puzzle is a step in the history: Back from the second is the
// first, as it is on any two pages.

import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api, errorText, getJson, isSignedOut, postJson, readJson } from "../app/api.ts";
import { fmtCount, fmtDate, type GameInfo } from "../app/format.ts";
import { Icon, type IconName } from "../app/icons.tsx";
import { DEFENCE_TAGS, humanTag, lichessTheme } from "../app/kinds.ts";
import { prefs, savePrefs } from "../app/prefs.ts";
import { follow, navigate, setAddress, usePlace } from "../app/router.ts";
import { session } from "../app/session.ts";
import { markSolved } from "../app/solved.ts";
import { keep, kept } from "../app/kept.ts";
import { useShell, type ScreenProps } from "../app/App.tsx";
import { PuzzlePanel, type PuzzleData } from "./PuzzlePanel.tsx";
import { BoardPlaceholder } from "./BoardScreen.tsx";

interface Item {
  puzzle: PuzzleData;
  game: GameInfo;
  blunder_tolerance?: number;
}

interface Reading {
  profile?: { lichess_username?: string; backfill_done?: boolean };
  history?: { analysed?: number; pending?: number; oldest_game_at?: number };
  lichess?: { games?: number; username?: string } | null;
}

const puzzleAt = (item: Item) => `${item.game.game_id}.${item.game.color}.${item.puzzle.ply}`;

/** The queue's filters, as the request writes them. */
function queueParams(): URLSearchParams {
  const params = new URLSearchParams({ limit: "20" });
  if (prefs.trainKind) params.set("kind", prefs.trainKind);
  if (prefs.trainSpeed) params.set("speed", prefs.trainSpeed);
  if (prefs.trainMotif) params.set("motif", prefs.trainMotif);
  return params;
}

/** The first page of the queue as it last came, and the puzzle on show:
 *  what Train opens on at once, before the server has said anything. */
interface KeptQueue {
  params: string;
  data: { puzzles?: Item[]; next?: number | null; waiting?: number; games?: number; drill_due?: number; reading?: Reading | null };
  at: string;
}

/** Take a solved puzzle out of what is kept, so the next open does not
 *  lead with it. */
function forgetKept(address: string) {
  const last = kept<KeptQueue>("train");
  if (!last) return;
  keep("train", { ...last, data: { ...last.data, puzzles: (last.data.puzzles || []).filter((item) => puzzleAt(item) !== address) } });
}

export function TrainScreen({ query }: ScreenProps) {
  const place = usePlace();
  const shell = useShell();
  // Everything the queue is, in one place the async loaders can read from
  // without stale closures; `version` redraws the screen when it changes.
  const q = useRef({
    items: [] as Item[],
    next: undefined as number | null | undefined,
    loading: false,
    exhausted: false,
    seq: 0,
    waiting: 0,
    games: 0,
    drillDue: 0,
    reading: null as Reading | null,
    wanted: query.get("at") || "",
  });
  const [, redraw] = useState(0);
  const refresh = useCallback(() => redraw((n) => n + 1), []);
  // Opened again on the same queue: the puzzle that was on show, at once, and
  // the server asked behind it (`catchUp`).
  const [start] = useState(() => {
    const last = kept<KeptQueue>("train");
    const s = q.current;
    if (!last || last.params !== queueParams().toString() || !(last.data.puzzles || []).length) return null;
    const items = last.data.puzzles || [];
    const at = s.wanted || last.at;
    const here = items.findIndex((item) => puzzleAt(item) === at);
    // Asked for a puzzle that is not in what was kept: that one, from the
    // server, the long way.
    if (s.wanted && here === -1) return null;
    s.items = items.slice();
    s.next = last.data.next;
    s.exhausted = last.data.next == null;
    s.waiting = last.data.waiting || 0;
    s.games = last.data.games || 0;
    s.drillDue = last.data.drill_due || 0;
    s.reading = last.data.reading || null;
    s.wanted = "";
    return { index: Math.max(0, here) };
  });
  const [index, setIndex] = useState(start ? start.index : 0);
  const [phase, setPhase] = useState<"loading" | "ready" | { error: string }>(start ? "ready" : "loading");
  const [solvedNow, setSolvedNow] = useState(0);
  const [filters, setFilters] = useState(0);

  const load = useCallback(async () => {
    const s = q.current;
    if (s.loading || s.exhausted) return;
    s.loading = true;
    const seq = ++s.seq;
    const params = queueParams();
    const first = s.next == null;
    if (s.next != null) params.set("before", String(s.next));
    try {
      const data = await readJson(await api("/api/queue?" + params));
      if (seq !== s.seq) return;
      if (first) keep("train", { params: params.toString(), data, at: s.items[0] ? puzzleAt(s.items[0]) : "" } satisfies KeptQueue);
      session.analysisVersion = data.analysis_version ?? session.analysisVersion;
      s.items.push(...(data.puzzles || []));
      s.next = data.next;
      s.exhausted = data.next == null;
      s.waiting = data.waiting || 0;
      s.games = data.games || 0;
      s.drillDue = data.drill_due || 0;
      s.reading = data.reading || s.reading;
      refresh();
    } catch (err) {
      if (isSignedOut(err)) return;
      setPhase({ error: errorText(err) });
    } finally {
      if (seq === s.seq) s.loading = false;
    }
  }, [refresh]);

  /** Where in the queue the puzzle with this address is. One not waiting any
   *  more is read out of its game's report and put at the head: an address
   *  that was worth keeping opens what it named. */
  const find = useCallback(async (at: string) => {
    const s = q.current;
    const here = s.items.findIndex((item) => puzzleAt(item) === at);
    if (here !== -1) return here;
    const [gameId, color, ply] = at.split(".");
    try {
      const report = await getJson(`/api/report/${encodeURIComponent(gameId!)}/${color}`);
      const puzzle = (report.puzzles || []).find((p: PuzzleData) => String(p.ply) === ply);
      if (!puzzle) return 0;
      s.items.unshift({
        puzzle,
        game: {
          game_id: report.game_id,
          color: report.color,
          white: report.white,
          black: report.black,
          result: report.result,
          speed: report.speed,
          time_control: report.time_control,
          played_at: report.played_at,
        },
        blunder_tolerance: report.config?.blunder_tolerance ?? 0.15,
      });
    } catch {
      // Gone: open the queue at its head.
    }
    return 0;
  }, []);

  const reload = useCallback(async () => {
    const s = q.current;
    s.seq++;
    s.loading = false;
    s.items = [];
    s.next = undefined;
    s.exhausted = false;
    setPhase("loading");
    await load();
    const seq = s.seq;
    const at = s.wanted ? await find(s.wanted) : 0;
    if (seq !== s.seq) return;
    s.wanted = "";
    setIndex(at);
    setPhase((p) => (typeof p === "object" ? p : "ready"));
    const item = s.items[at];
    setAddress({ at: item ? puzzleAt(item) : null });
  }, [find, load]);

  /**
   * The queue as the server has it now, behind the puzzle that was shown from
   * what was kept. The puzzle on the board stays where it is — it may be half
   * solved — and the rest of the queue, the counts and the filters come up to
   * date around it.
   */
  const catchUp = useCallback(async () => {
    const s = q.current;
    const shown = s.items[index];
    const seq = ++s.seq;
    s.loading = true;
    try {
      const params = queueParams();
      const data = await readJson(await api("/api/queue?" + params));
      if (seq !== s.seq) return;
      session.analysisVersion = data.analysis_version ?? session.analysisVersion;
      const items: Item[] = data.puzzles || [];
      keep("train", { params: params.toString(), data, at: shown ? puzzleAt(shown) : "" } satisfies KeptQueue);
      let here = shown ? items.findIndex((item) => puzzleAt(item) === puzzleAt(shown)) : 0;
      if (shown && here === -1) {
        items.unshift(shown);
        here = 0;
      }
      s.items = items;
      s.next = data.next;
      s.exhausted = data.next == null;
      s.waiting = data.waiting || 0;
      s.games = data.games || 0;
      s.drillDue = data.drill_due || 0;
      s.reading = data.reading || s.reading;
      setIndex(Math.max(0, here));
      refresh();
    } catch (err) {
      // Offline: what was kept is what there is.
      if (isSignedOut(err)) return;
    } finally {
      if (seq === s.seq) s.loading = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  // Whatever is on the board is what the next open starts from: reached by
  // Next, by Back, or by a link.
  const shownAt = q.current.items[index] ? puzzleAt(q.current.items[index]!) : "";
  // A puzzle opened by its address need not be in the queue — solved since,
  // or outside the filters — so it is kept with it, at the head, as it is
  // shown.
  useEffect(() => {
    if (!shownAt || phase !== "ready") return;
    const last = kept<KeptQueue>("train");
    if (!last) return;
    const puzzles = last.data.puzzles || [];
    const shown = q.current.items.find((item) => puzzleAt(item) === shownAt);
    const missing = shown && !puzzles.some((item) => puzzleAt(item) === shownAt);
    if (last.at === shownAt && !missing) return;
    keep("train", { ...last, at: shownAt, data: missing ? { ...last.data, puzzles: [shown!, ...puzzles] } : last.data });
  }, [shownAt, phase]);

  const opened = useRef(false);
  useEffect(() => {
    if (!opened.current && start) {
      opened.current = true;
      const item = q.current.items[index];
      setAddress({ at: item ? puzzleAt(item) : null });
      void catchUp();
      return;
    }
    opened.current = true;
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, filters]);

  const show = useCallback(
    (i: number) => {
      const s = q.current;
      const at = Math.max(0, i);
      setIndex(at);
      const item = s.items[at];
      setAddress({ at: item ? puzzleAt(item) : null }, { push: true });
      // Keep the next batch coming before the current one runs out.
      if (s.items.length - at <= 3 && !s.exhausted) void load();
    },
    [load]
  );

  // Back and Forward between puzzles of the queue that is loaded.
  const seenMove = useRef(place.moved);
  useEffect(() => {
    if (seenMove.current === place.moved) return;
    seenMove.current = place.moved;
    const at = new URLSearchParams(place.search).get("at");
    const here = at ? q.current.items.findIndex((item) => puzzleAt(item) === at) : -1;
    if (here !== -1) setIndex(here);
    else {
      q.current.wanted = at || "";
      void reload();
    }
  }, [place.moved, place.search, reload]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.target as HTMLElement).closest("input, textarea, select, [contenteditable]")) return;
      if (e.key === "ArrowRight") show(index + 1);
      else if (e.key === "ArrowLeft" && index > 0) show(index - 1);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [index, show]);

  const s = q.current;
  const changeFilters = () => {
    savePrefs();
    setFilters((n) => n + 1);
  };
  const item = s.items[index];
  const stats = <TrainStats waiting={s.waiting} games={s.games} drillDue={s.drillDue} at={item ? index : -1} of={s.items.length} more={!s.exhausted} solvedNow={solvedNow} />;
  const filterBar = <TrainFilters onChange={changeFilters} />;

  if (typeof phase === "object") {
    return (
      <div className="screen train-screen">
        <div className="notice error">{phase.error}</div>
      </div>
    );
  }
  if (phase === "loading") {
    return (
      <div className="screen train-screen">
        <Head />
        <div className="train-bar wide-bar boot-train-bar" />
        <BoardPlaceholder
          status="Finding what is waiting…"
          lead={
            <button type="button" className="btn menu-btn" onClick={() => shell.openMenu()}>
              <Icon name="grid" />
              Menu
            </button>
          }
          slots={
            <>
              <button type="button" className="btn" disabled>
                <Icon name="prev" />
                <span className="long">Previous</span>
                <span className="short">Prev</span>
              </button>
              <button type="button" className="btn advance" disabled>
                <Icon name="skip" />
                Skip
              </button>
            </>
          }
        />
      </div>
    );
  }

  if (!item) {
    const analysed = s.reading?.history?.analysed || 0;
    return (
      <div className={"screen train-screen" + (!s.items.length && !(analysed > 0) ? " is-new" : "")}>
        <Head />
        <div className="train-bar">
          <div className="train-counts">{stats}</div>
          {filterBar}
        </div>
        {!s.items.length ? (
          <Empty
            reading={s.reading}
            waiting={s.waiting}
            drillDue={s.drillDue}
            onReading={(data) => {
              s.reading = data;
              refresh();
            }}
            onClearFilters={() => {
              prefs.trainKind = "";
              prefs.trainSpeed = "";
              prefs.trainMotif = "";
              changeFilters();
            }}
            poll={async () => {
              const before = s.reading?.history?.analysed || 0;
              s.seq++;
              s.next = undefined;
              s.exhausted = false;
              s.items = [];
              s.loading = false;
              await load();
              if (s.items.length) return show(0);
              if ((s.reading?.history?.analysed || 0) !== before || s.reading?.profile?.backfill_done) refresh();
            }}
          />
        ) : (
          <div className="card finished">
            <h2>That's everything loaded.</h2>
            <p className="muted">{s.exhausted ? "Nothing else is waiting with these filters." : "Load the next batch, or start over from the newest game."}</p>
            <div className="btn-row" style={{ justifyContent: "center", marginTop: "16px" }}>
              {!s.exhausted ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={async () => {
                    await load();
                    show(index);
                  }}
                >
                  Load more
                </button>
              ) : null}
              <button type="button" className="btn" onClick={() => void reload()}>
                <Icon name="refresh" />
                Start over
              </button>
              <button type="button" className="btn" onClick={() => navigate("/games")}>
                Games
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="screen train-screen">
      <Head />
      <div className="train-bar wide-bar">
        <div className="train-counts">{stats}</div>
        {filterBar}
      </div>
      <PuzzlePanel
        key={puzzleAt(item)}
        puzzle={item.puzzle}
        game={item.game}
        tolerance={item.blunder_tolerance}
        solved={false}
        showGame
        onSolved={() => {
          forgetKept(puzzleAt(item));
          if (markSolved(item.game.game_id!, item.game.color, item.puzzle.ply)) {
            setSolvedNow((n) => n + 1);
            s.waiting = Math.max(0, s.waiting - 1);
            refresh();
          }
        }}
        onTag={() => changeFilters()}
        lead={
          <button type="button" className="btn menu-btn" onClick={() => shell.openMenu()}>
            <Icon name="grid" />
            Menu
          </button>
        }
        slots={(solved) => <TrainSlots index={index} solved={solved} onPrev={() => show(index - 1)} onNext={() => show(index + 1)} />}
        sheetBottom={
          <div className="narrow-only sheet-filters">
            <div className="train-counts">{stats}</div>
            {filterBar}
          </div>
        }
      />
    </div>
  );
}

/** Previous, and Skip — which is Next once the puzzle is solved: one slot,
 *  taken in turn, one width, so the button that moves on is where it was. */
function TrainSlots({ index, solved, onPrev, onNext }: { index: number; solved: boolean; onPrev: () => void; onNext: () => void }) {
  return (
    <>
      <button type="button" className="btn" disabled={index === 0} onClick={onPrev}>
        <Icon name="prev" />
        <span className="long">Previous</span>
        <span className="short">Prev</span>
      </button>
      {solved ? (
        <button type="button" className="btn btn-primary advance" onClick={onNext}>
          Next
          <Icon name="next" />
        </button>
      ) : (
        <button type="button" className="btn advance" onClick={onNext}>
          <Icon name="skip" />
          Skip
        </button>
      )}
    </>
  );
}

function Head() {
  return (
    <div className="screen-head">
      <div>
        <h1>Train</h1>
        <p>Positions from your own games, newest first.</p>
      </div>
    </div>
  );
}

/** Where you are in the queue and what this sitting has come to. */
function TrainStats({
  waiting,
  games,
  drillDue,
  at,
  of,
  more,
  solvedNow,
}: {
  waiting: number;
  games: number;
  drillDue: number;
  at: number;
  of: number;
  more: boolean;
  solvedNow: number;
}) {
  return (
    <>
      <div className="train-stats">
        <span>
          <b className="num">{waiting}</b> waiting
          <span className="wide-only">
            {" "}
            in <b className="num">{games}</b> game{games === 1 ? "" : "s"}
          </span>
        </span>
        {drillDue ? (
          <span className="train-drill">
            <b className="num">{drillDue}</b>
            <span className="wide-only"> opening move{drillDue === 1 ? "" : "s"}</span> due — <a {...follow("/openings/drill")}>Drill</a>
          </span>
        ) : null}
      </div>{" "}
      <span className="train-pos">
        {at >= 0 && of ? `${Math.min(at + 1, of)} / ${of}${more ? "+" : ""}` : ""}
        {solvedNow ? ` · ${solvedNow} solved` : null}
        {solvedNow ? <span className="wide-only"> now</span> : null}
      </span>
    </>
  );
}

const KINDS = [
  { value: "", label: "All" },
  { value: "tactic", label: "Tactics" },
  { value: "only_move", label: "Only moves" },
  { value: "blunder", label: "Blunders" },
  { value: "opening", label: "Openings" },
];
const SPEEDS: [string, string][] = [
  ["", "Any speed"],
  ["classical", "Classical"],
  ["rapid", "Rapid"],
  ["blitz", "Blitz"],
  ["bullet", "Bullet"],
];

function TrainFilters({ onChange }: { onChange: () => void }) {
  return (
    <div className="train-filters">
      <div className="chips">
        {/* A motif chosen on the "what you miss" page leads the row, pressed,
            and goes when it is pressed again. */}
        {prefs.trainMotif ? (
          <button
            type="button"
            className="chip chip-motif"
            aria-pressed="true"
            title="Showing only this motif. Press to show everything again."
            onClick={() => {
              prefs.trainMotif = "";
              onChange();
            }}
          >
            {humanTag(prefs.trainMotif)}
            <span className="chip-x" aria-hidden="true">
              ×
            </span>
          </button>
        ) : null}
        {KINDS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={"chip " + opt.value}
            aria-pressed={prefs.trainKind === opt.value}
            onClick={() => {
              prefs.trainKind = opt.value;
              onChange();
            }}
          >
            {opt.value ? <span className="dot" /> : null}
            {opt.label}
          </button>
        ))}
      </div>
      {/* The select itself stays: a phone gives it a proper wheel, and a
          keyboard gives it a real control. It only goes invisible over the
          text, which is what shows the chosen speed. */}
      <span className="chip chip-speed" data-on={String(Boolean(prefs.trainSpeed))}>
        <select
          className="chip-select"
          aria-label="Speed"
          value={prefs.trainSpeed}
          onChange={(e) => {
            prefs.trainSpeed = e.target.value;
            onChange();
          }}
        >
          {SPEEDS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <span className="chip-select-text" aria-hidden="true">
          {(SPEEDS.find(([value]) => value === prefs.trainSpeed) || SPEEDS[0]!)[1]}
        </span>
        <span className="chip-caret" aria-hidden="true">
          ▾
        </span>
      </span>
    </div>
  );
}

// --- NOTHING TO SOLVE, AND WHAT TO DO ABOUT IT ---

/**
 * What there is to do that needs none of your games: the doors open to
 * someone on their first day, to someone whose history is still being read,
 * and to someone who has solved everything they had.
 */
export function WaysOn({ drillDue = 0, motif = "" }: { drillDue?: number; motif?: string }) {
  const way = (key: string, attrs: Record<string, unknown>, glyph: IconName, title: string, text: string) => (
    <a key={key} className="way" {...attrs}>
      <span className="way-icon">
        <Icon name={glyph} />
      </span>
      <span className="way-text">
        <b>{title}</b>
        <span>{text}</span>
      </span>
    </a>
  );
  return (
    <div className="ways">
      {drillDue
        ? way("drill", follow("/openings/drill"), "target", `Drill ${drillDue} opening move${drillDue === 1 ? "" : "s"}`, "The lines you are learning, due today.")
        : way("learn", follow("/openings/learn"), "book", "Learn an opening", "Pick one, get a course built from 82 million games at your level, drill it, then play it against a bot.")}
      {way("analyse", follow("/analyse"), "search", "Analyse one game", "Paste a Lichess link or a PGN. About a minute, and its mistakes become puzzles.")}
      {motif
        ? way(
            "lichess",
            { href: `https://lichess.org/training/${lichessTheme(motif)}`, target: "_blank", rel: "noopener" },
            "ext",
            `More ${humanTag(motif)} puzzles`,
            "What you miss most in your own games, from Lichess's millions."
          )
        : way("lichess", { href: "https://lichess.org/training", target: "_blank", rel: "noopener" }, "ext", "Puzzles on Lichess", "Theirs, until yours are ready.")}
    </div>
  );
}

/**
 * Who are you on Lichess? Asked where the answer is needed — on the empty
 * screen itself. The server checks the name with Lichess, so a typo is said
 * now and not discovered as a page that reads games for ever.
 */
export function LichessNameForm({ onSaved }: { onSaved: (data: any) => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<{ text: string; working: boolean } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    const wanted = name.trim();
    if (!wanted) return input.current?.focus();
    // The label stays, so the field beside it does not shrink: what is
    // happening is said under it.
    setBusy(true);
    setProblem({ text: "Asking Lichess…", working: true });
    try {
      const res = await postJson("/api/profile", { lichess_username: wanted });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.error || "Could not save that name.");
      onSaved(data);
    } catch (err) {
      if (isSignedOut(err)) return;
      setProblem({ text: errorText(err), working: false });
      setBusy(false);
    }
  }
  return (
    <div>
      <form className="welcome-form" onSubmit={submit}>
        <input
          ref={input}
          className="field"
          type="text"
          placeholder="Lichess username"
          autoComplete="off"
          spellCheck={false}
          autoCapitalize="off"
          aria-label="Lichess username"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button type="submit" className="btn btn-primary" disabled={busy}>
          Read my games
        </button>
      </form>
      {problem ? <p className={"welcome-problem" + (problem.working ? " working" : "")}>{problem.text}</p> : null}
    </div>
  );
}

/**
 * Nothing to solve. Which nothing it is decides what the screen says:
 * filters that hide everything, nobody to read games for yet, games still
 * being read, or everything there was, solved. Only the first is a dead end
 * of the person's own making; the rest all have somewhere to go.
 */
function Empty({
  reading,
  waiting,
  drillDue,
  onReading,
  onClearFilters,
  poll,
}: {
  reading: Reading | null;
  waiting: number;
  drillDue: number;
  onReading: (data: Reading) => void;
  onClearFilters: () => void;
  poll: () => Promise<void>;
}) {
  const profile = reading?.profile || {};
  const history = reading?.history || {};
  const analysed = history.analysed || 0;
  const name = profile.lichess_username || "";
  const filtered = Boolean(prefs.trainKind || prefs.trainSpeed || prefs.trainMotif);
  const walking = Boolean(name) && !profile.backfill_done;
  const readingNow = walking || Boolean(name && !analysed && (history.pending || 0) > 0);
  const [motif, setMotif] = useState("");

  // While games are being read, look again every quarter of a minute: the
  // first puzzle should arrive in front of the person waiting for it.
  useEffect(() => {
    if (!readingNow) return;
    const timer = setInterval(() => {
      if (!document.hidden) void poll();
    }, 15000);
    return () => clearInterval(timer);
  }, [readingNow, poll]);

  // What they miss most, for the third door: asked for only here.
  useEffect(() => {
    if (!analysed || readingNow || filtered) return;
    getJson("/api/motifs")
      .then((data) => {
        const top = (data.motifs || []).find((m: { tag: string }) => !DEFENCE_TAGS.has(m.tag));
        if (top) setMotif(top.tag);
      })
      .catch(() => {});
  }, [analysed, readingNow, filtered]);

  if (filtered && analysed > 0) {
    return (
      <div className="empty card">
        <h2>Nothing waiting with these filters</h2>
        <p>
          {fmtCount(waiting)} puzzle{waiting === 1 ? " is" : "s are"} waiting without them.
        </p>
        <div className="btn-row">
          <button type="button" className="btn btn-primary" onClick={onClearFilters}>
            Show everything
          </button>
        </div>
      </div>
    );
  }

  if (!name && !analysed) {
    return (
      <div className="card welcome">
        <h2>Your own mistakes, replayed as puzzles</h2>
        <p className="welcome-lead">
          Blundermine reads your Lichess games, finds the moments you walked past a win or gave one away, and hands them back to you to solve. Tell it who you are there.
        </p>
        <LichessNameForm onSaved={onReading} />
        <p className="welcome-fine">Only your public games are read. Nothing is posted to Lichess, and nothing there changes.</p>
        <h3>No games yet, or no patience?</h3>
        <WaysOn drillDue={drillDue} />
      </div>
    );
  }

  if (readingNow) {
    const found = reading?.lichess;
    const nothingThere = found && found.games === 0;
    return (
      <div className="card welcome">
        <h2>{nothingThere ? `${name} has not played on Lichess yet` : `Reading ${name}'s games`}</h2>
        {nothingThere ? null : (
          <div className="welcome-progress" role="progressbar" aria-label="Reading games">
            <span />
          </div>
        )}
        <p className="welcome-lead">
          {nothingThere
            ? "Play a game there and it is here a few minutes after it ends. This page will show its puzzles on its own."
            : analysed
              ? `${fmtCount(analysed)} game${analysed === 1 ? "" : "s"} read so far${history.oldest_game_at ? `, back to ${fmtDate(history.oldest_game_at)}` : ""}. It is still working back through your history, about a minute a game, and new puzzles appear here on their own.`
              : `${found?.games ? `Found ${found.username || name}: ${fmtCount(found.games)} games on Lichess. ` : ""}The first of them are with the engine now — about a minute each, slower games first. The first puzzles appear here on their own, usually within a couple of minutes.`}
        </p>
        <h3>Meanwhile</h3>
        <WaysOn drillDue={drillDue} />
      </div>
    );
  }

  return (
    <div className="card welcome">
      <h2>{analysed ? "All caught up" : name ? "No games to read yet" : "Nothing waiting"}</h2>
      <p className="welcome-lead">
        {analysed
          ? `Nothing is waiting from your ${fmtCount(analysed)} game${analysed === 1 ? "" : "s"}. A new game is analysed on its own, a few minutes after it ends${(history.pending || 0) > 0 ? " — one is with the engine now" : ""}.`
          : name
            ? `Nothing of ${name}'s on Lichess so far. A game played there is here a few minutes after it ends.`
            : "Nobody's games are being read. Give your Lichess name in Settings and they are analysed on their own."}
      </p>
      <h3>Until then</h3>
      <WaysOn drillDue={drillDue} motif={motif} />
    </div>
  );
}
